/***
*
*   ALERTS
*   Full page view of all system alerts with filtering
*
**********/

import { useState } from 'react';
import {
  Animate,
  Card,
  Badge,
  Button,
  Icon,
  Paginate,
  useNavigate,
  useDashboardAlerts
} from 'components/lib';
import Style from 'components/dashboard/dashboard.tailwind.js';

export function Alerts(props) {
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    page: 1,
    limit: 20
  });

  const alerts = useDashboardAlerts(filters);
  const { alerts: alertsList = [], summary = {} } = alerts.data || {};

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset page when filter changes
    }));
  };

  const handleViewOrg = (workspaceId) => {
    navigate(`/workspaces/${workspaceId}`);
  };

  const getAlertConfig = (type, severity) => {
    const configs = {
      'storage': { icon: 'alert-triangle', color: 'red', label: 'Over Quota', bgClass: Style.alertItemRed },
      'storage_warning': { icon: 'alert-triangle', color: 'orange', label: 'Storage Warning', bgClass: Style.alertItemOrange },
      'inactive': { icon: 'clock', color: 'orange', label: 'Inactive', bgClass: Style.alertItemOrange },
      'trial_expiring': { icon: 'calendar', color: 'blue', label: 'Trial Expiring', bgClass: Style.alertItemBlue },
      'trial_expired': { icon: 'calendar', color: 'red', label: 'Trial Expired', bgClass: Style.alertItemRed },
      'employee_limit': { icon: 'users', color: 'orange', label: 'Employee Limit', bgClass: Style.alertItemOrange },
      'job_opening_limit': { icon: 'briefcase', color: 'orange', label: 'Job Limit', bgClass: Style.alertItemOrange },
      'payment_failed': { icon: 'credit-card', color: 'red', label: 'Payment Failed', bgClass: Style.alertItemRed },
    };

    return configs[type] || {
      icon: 'info',
      color: severity === 'high' ? 'red' : 'orange',
      label: type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Alert',
      bgClass: severity === 'high' ? Style.alertItemRed : Style.alertItemOrange
    };
  };

  const formatAlertDetail = (alert) => {
    const { type, details } = alert;

    if (type === 'storage' && details) {
      return `${details.used}MB / ${details.allocated}MB (+${details.overage}MB over)`;
    }
    if (type === 'trial_expired' && details) {
      return `Expired ${Math.abs(details.days_remaining)} days ago • ${details.user_count} users`;
    }
    if (type === 'trial_expiring' && details) {
      return `${details.days_remaining} days remaining`;
    }
    if (type === 'inactive' && details?.last_active) {
      return `Last active: ${new Date(details.last_active).toLocaleDateString()}`;
    }
    if (type === 'employee_limit' && details) {
      return `${details.current}/${details.limit} employees`;
    }
    if (type === 'job_opening_limit' && details) {
      return `${details.current}/${details.limit} openings`;
    }

    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Alert type options from summary
  const alertTypes = summary.by_type ? Object.keys(summary.by_type) : [];
  const totalPages = Math.ceil((summary.total || 0) / filters.limit);

  return (
    <Animate type='pop'>
      {/* Header */}
      <div className={Style.alertsPageHeader}>
        <div>
          <h1 className={Style.alertsPageTitle}>System Alerts</h1>
          <p className="text-slate-500 text-sm mt-1">
            {summary.total || 0} total alerts
          </p>
        </div>
        <Button
          text="Back to Dashboard"
          icon="arrow-left"
          color="secondary"
          action={() => navigate('/dashboard')}
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-slate-800">{summary.total || 0}</div>
            <div className="text-sm text-slate-500">Total Alerts</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">{summary.high || 0}</div>
            <div className="text-sm text-slate-500">High Severity</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{summary.medium || 0}</div>
            <div className="text-sm text-slate-500">Medium Severity</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-600">{summary.low || 0}</div>
            <div className="text-sm text-slate-500">Low Severity</div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className={Style.alertsFilters}>
        <select
          className={Style.filterSelect}
          value={filters.type}
          onChange={(e) => handleFilterChange('type', e.target.value)}
        >
          <option value="">All Types</option>
          {alertTypes.map(type => (
            <option key={type} value={type}>
              {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              {summary.by_type[type] > 0 && ` (${summary.by_type[type]})`}
            </option>
          ))}
        </select>

        <select
          className={Style.filterSelect}
          value={filters.severity}
          onChange={(e) => handleFilterChange('severity', e.target.value)}
        >
          <option value="">All Severities</option>
          <option value="high">High ({summary.high || 0})</option>
          <option value="medium">Medium ({summary.medium || 0})</option>
          <option value="low">Low ({summary.low || 0})</option>
        </select>

        <select
          className={Style.filterSelect}
          value={filters.limit}
          onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
        >
          <option value={10}>10 per page</option>
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>

      {/* Alerts List */}
      <Card loading={alerts.loading}>
        <div className={Style.alertsGrid}>
          {alertsList.length > 0 ? (
            alertsList.map((alert, index) => {
              const config = getAlertConfig(alert.type, alert.severity);
              const detail = formatAlertDetail(alert);

              return (
                <div key={index} className={`${Style.alertItem} ${config.bgClass}`}>
                  <div className={Style.alertIcon}>
                    <Icon
                      image={config.icon}
                      color={config.color}
                      size={20}
                    />
                  </div>
                  <div className={Style.alertContent}>
                    <div className={Style.alertName}>
                      {alert.message}
                    </div>
                    <div className={Style.alertMeta}>
                      <Badge text={config.label} color={config.color} />
                      <Badge
                        text={alert.severity}
                        color={alert.severity === 'high' ? 'red' : alert.severity === 'medium' ? 'orange' : 'gray'}
                      />
                      {detail && (
                        <span className={Style.alertDetail}>{detail}</span>
                      )}
                      <span className={Style.alertDetail}>
                        {formatDate(alert.date)}
                      </span>
                    </div>
                  </div>
                  {alert.workspace_id && (
                    <Button
                      text=""
                      icon="eye"
                      size="small"
                      color="secondary"
                      action={() => handleViewOrg(alert.workspace_id)}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className={Style.noAlerts}>
              <Icon image="check-circle" color="green" size={32} />
              <span>No alerts match your filters</span>
            </div>
          )}
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={Style.alertsPagination}>
          <Paginate
            currentPage={filters.page}
            totalPages={totalPages}
            onChange={(page) => handleFilterChange('page', page)}
          />
        </div>
      )}
    </Animate>
  );
}